import { Component, OnInit, ViewEncapsulation, DoCheck, ViewChild } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../../models/base-modal-component';
import { Endpoint, EntityType, HelpIndex, MessageType, TemplateOperationType, UpdateStatusType } from '../../../../models/enums';
import { Template, TemplateType } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { CreateEditEntityModalComponent } from '../../create-edit-entity-modal/create-edit-entity-modal.component';
import { ExportMetadataProfile } from '../../../../models/export-metadata-profile';
import { LoadFileProjectTemplate } from '../../../../models/load-file-project-template';
import { Permissions, ActionType } from '../../../../models/permission';
import { GenericTreeComponent } from '../../../generic-tree/generic-tree.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { TaskStatus } from '../../../../models/task';

@Component({
    standalone: false,
    selector: 'res-save-as-load-from-template',
    templateUrl: './save-as-load-from-template.component.html',
    styleUrls: ['./save-as-load-from-template.component.scss'],
    encapsulation: ViewEncapsulation.None

})
export class SaveAsLoadFromTemplateComponent extends BaseModalComponent implements OnInit, DoCheck {
    @ViewChild('orgTemplatesInSystemContext') orgsTree: GenericTreeComponent;
    isBusy = false;
    isBusyMessage = 'Loading...';
    orgTemplates = [];
    sysTemplates = [];
    projectProfiles = [];
    hideSysTemplates: boolean;
    newTemplateVisible: boolean;
    templateType: string;
    templateTab: string;
    operationType: TemplateOperationType;
    parentTemplate: any;
    selectedTemplate: any;
    modalTitle: string;
    // 'save-to' for autoIds, 'save-as' for everything else
    modalAutomationId = 'save-to-load-from-template-modal';
    orgTemplatesAutoIdPrefix = 'save-to-load-from-organization-template';
    modalContainerAutomationId: string;
    secondaryButtonLabel = 'Cancel';
    primaryButtonLabel = 'OK';
    modalClass = 'save-as-load-from-template-modal';
    modalHelpIndex = HelpIndex.load_from_template_dlg;
    hideProjectTemplates = true;
    hasSysPermissions = true;
    hasOrgPermissions = true;
    isSystemContext: boolean;
    templateChildHandleToSelect: string;
    isWorkflowSaveToTemplate = false;
    isWorkflowLoadFromTemplate = false;

    constructor(
        private modalService: BsModalService,
        private restService: RestService,
        private commonService: CommonService,
        private modalRef: BsModalRef
    ) {
        super(modalService, modalRef);
    }

    ngOnInit() {
        this.hideProjectTemplates = true;
        if (this.modalData) {
            this.isSystemContext = this.modalData.isSystemContext;
            this.operationType = <TemplateOperationType>this.modalData.operationType;
            this.getTemplateTypeFromModalData();
            const isSaveAsOperation = this.operationType === TemplateOperationType.saveAs;
            this.isWorkflowSaveToTemplate = isSaveAsOperation && this.templateType === EntityType.workFlow;
            if (this.templateType === EntityType.workFlow) {
                this.templateType = TemplateType.WORKFLOWS;
                this.isWorkflowLoadFromTemplate = !isSaveAsOperation;
            }
            this.hasSysPermissions = Permissions.checkUserPermission(Permissions.getSystemObjectListTypeForTemplateType(this.templateType as TemplateType), isSaveAsOperation ? ActionType.CREATE : ActionType.VIEW);
            if (this.modalData.permissions && this.modalData.permissions.length > 0) {
                this.hasOrgPermissions = Permissions.checkUserPermission(this.modalData.permissions[0].permissionType.replace('PRJ_', 'ORG_'), isSaveAsOperation ? ActionType.CREATE : ActionType.VIEW);
            }
            if (isSaveAsOperation) {
                this.hideSysTemplates = !this.isSystemContext || !this.hasSysPermissions;
                this.newTemplateVisible = true;
                this.modalTitle = 'Save to Template';
                this.modalHelpIndex = HelpIndex.save_as_template_dlg;
                this.modalContainerAutomationId = 'save-to-load-from-template';
                if (this.modalData.selectedItem.projectId) {
                    if (this.templateType === 'LOADFILE_SETTINGS' || this.templateType === 'EXPORT_FIELDS') {
                        this.hideProjectTemplates = false;
                        this.templateTab = 'project';
                    }
                } else {
                    this.templateTab = 'organization';
                }
            } else {
                this.modalTitle = 'Load from Template';
                if (this.templateType === 'ALIAS_LISTS' || this.templateType === 'LOADFILE_SETTINGS') {
                    this.hideSysTemplates = true;
                }
                if (this.modalData.selectedItem?.projectId) {
                    if (this.templateType === 'LOADFILE_SETTINGS' || this.templateType === 'EXPORT_FIELDS') {
                        this.hideProjectTemplates = false;
                        this.templateTab = 'project';
                    }
                } else {
                    this.templateTab = 'organization';
                }
                this.modalContainerAutomationId = 'save-to-load-from-template';
            }
            this.parentTemplate = this.modalData.selectedItem;
            this.loadTemplates();
        }
    }

    ngDoCheck() {
        this.onResize();
    }

    onResize() {
        const modal = document.getElementsByClassName('save-as-load-from-template-modal');
        const container = document.getElementsByClassName('modal-body');

        if (modal && modal[0] && modal[0].clientWidth > 700) {
            container[1].setAttribute('style', 'width:auto');
        }
    }

    getTemplateTypeFromModalData(): void {
        if (this.modalData) {
            if (this.modalData.templateType) {
                this.templateType = this.modalData.templateType;
            } else {
                this.templateType = this.modalData.selectedItem ? this.modalData.selectedItem.templateType : null;
            }
        }
    }

    newTemplate(selectedOrgName?: string): void {
        if (this.templateTab === 'project') {
            if (this.templateType === 'EXPORT_FIELDS') {
                const modalData = new ExportMetadataProfile({ projectId: this.modalData.selectedItem.projectId });
                Object.assign(modalData, { isSystemSettingsContext: this.isSystemContext });
                this.commonService.openModal(CreateEditEntityModalComponent, { modalData: modalData }).then(result => {
                    if (result.modalResult && result.modalData && result.modalData.profile) {
                        this.loadTemplates(result.modalData.profile.handle);
                    }
                });
            } else if (this.templateType === 'LOADFILE_SETTINGS') {
                const modalData = new LoadFileProjectTemplate({ projectId: this.modalData.selectedItem.projectId });
                Object.assign(modalData, { isSystemSettingsContext: this.isSystemContext });
                this.commonService.openModal(CreateEditEntityModalComponent, { modalData: modalData }).then(result => {
                    if (result.modalResult && result.modalData && result.modalData.profile) {
                        this.loadTemplates(result.modalData.profile.id);
                    }
                });
            }
        } else {
            this.commonService.openModal(CreateEditEntityModalComponent, {
                modalData: new Template({
                    templateType: <TemplateType>this.templateType,
                    scope: this.isSystemContext && this.templateTab === 'system' ? 'SYSTEM_LEVEL' : 'ORG_LEVEL',
                    orgId: selectedOrgName ? selectedOrgName : (this.commonService?.currentUser?.customerName || this.modalData?.currentOrg),
                })
            }).then(result => {
                if (result?.modalResult && result?.modalData?.template) {
                    if (this.isSystemContext && this.templateTab === 'organization') {
                        this.templateChildHandleToSelect = result.modalData.template.handle;
                        this.getTreeData(selectedOrgName);
                    } else {
                        this.loadTemplates(result.modalData.template.handle);
                    }
                }
            });
        }
    }

    setTemplateTab(value: string): void {
        this.templateTab = value;
    }

    isPrimaryButtonEnabled(): boolean {
        return this.selectedTemplate && !this.isBusy;
    }

    setSelectedTemplate(selected: any): void {
        this.selectedTemplate = selected;
    }

    loadTemplates(handleToSelect?: string): void {
        if (this.parentTemplate) {
            const scope = this.isSystemContext && this.templateTab === 'system' ? 'SYSTEM_LEVEL' : 'ORG_LEVEL';
            const requestData = {
                scope: scope,
                tempType: this.templateType,
                contextHandle: scope === 'SYSTEM_LEVEL' ? 'super_system_customer' : this.commonService?.currentUser?.customerName,
                systemScope: scope === 'SYSTEM_LEVEL'
            };
            if (this.isSystemContext) {
                this.getTreeData();
            } else if (this.hasOrgPermissions) {
                this.isBusy = true;
                this.restService.post(Endpoint.orgManager_listTemplates, requestData).then(response => {
                    if (response && response.templates) {
                        this.orgTemplates = response.templates;
                        // auto select newly created template if any
                        if (handleToSelect) {
                            const idx = this.orgTemplates.findIndex(t => t.handle === handleToSelect);
                            if (idx !== -1) {
                                this.setSelectedTemplate(this.orgTemplates[idx]);
                            }
                        }
                        this.isBusy = false;
                    }
                }, () => {
                    this.isBusy = false;
                });
            }
            if (this.modalData.selectedItem.projectId && (this.templateType === 'LOADFILE_SETTINGS' || this.templateType === 'EXPORT_FIELDS')) {
                if (this.templateType === 'LOADFILE_SETTINGS') {
                    this.isBusy = true;
                    const handle = this.modalData.selectedItem.projectId;
                    this.restService.post(Endpoint.projectManager_listLoadFileMetadataProfiles, { handle: handle, systemScope: scope === 'SYSTEM_LEVEL' }).then(response => {
                        if (response && response.profiles) {
                            this.projectProfiles = response.profiles;
                            // auto select newly created profile if any
                            if (handleToSelect) {
                                const idx = this.projectProfiles.findIndex(t => t.id === handleToSelect);
                                if (idx !== -1) {
                                    this.setSelectedTemplate(this.projectProfiles[idx]);
                                }
                            }
                            this.isBusy = false;
                        }
                    }, () => {
                        this.isBusy = false;
                    });
                } else if (this.templateType === 'EXPORT_FIELDS') {
                    this.isBusy = true;
                    const handle = this.modalData.selectedItem.projectId;
                    this.restService.post(Endpoint.projectManager_listExportMetadataProfile, { handle: handle, systemScope: scope === 'SYSTEM_LEVEL' }).then(response => {
                        if (response && response.profiles) {
                            this.projectProfiles = response.profiles;
                            // auto select newly created template if any
                            if (handleToSelect) {
                                const idx = this.projectProfiles.findIndex(t => t.handle === handleToSelect);
                                if (idx !== -1) {
                                    this.setSelectedTemplate(this.projectProfiles[idx]);
                                }
                            }
                            this.isBusy = false;
                        }
                    }, () => {
                        this.isBusy = false;
                    });
                }
            }

            if ((this.operationType === TemplateOperationType.loadFrom && this.hasSysPermissions) || this.isSystemContext) {
                requestData.scope = 'SYSTEM_LEVEL';
                requestData.systemScope = true;
                requestData.contextHandle = this.commonService.currentUser?.importedFromSystemUser ? 'super_system_customer' : this.commonService.currentUser.originalCustomerName;
                this.isBusy = true;
                if (!this.hasOrgPermissions && this.hideProjectTemplates) {
                    this.setTemplateTab('system');
                }
                this.restService.post(Endpoint.orgManager_listTemplates, requestData).then(response => {
                    if (response && response.templates) {
                        this.sysTemplates = response.templates;
                        this.isBusy = false;
                        // auto select newly created template if any
                        if (handleToSelect) {
                            const idx = this.sysTemplates.findIndex(t => t.handle === handleToSelect);
                            if (idx !== -1) {
                                this.setSelectedTemplate(this.sysTemplates[idx]);
                            }
                        }
                    }
                }, () => {
                    this.isBusy = false;
                });
            }
        }
    }

    selectedNodeChangedCallback = (node: GenericTreeNode) => {
        if (node) {
            if (node.name === 'New Template ...') {
                this.newTemplate(node.bundle ? node.bundle : null);
            } else {
                this.selectedTemplate = node.data;
            }
        }
    }

    finishLoadingTreeDataCallback = (node: GenericTreeNode): void => {
        if (this.operationType === TemplateOperationType.saveAs && node?.children) {
            node.children.unshift(new GenericTreeNode({
                name: 'New Template ...',
                icon: 'fa-plus',
                iconColor: 'color-green-04',
                hasExpanderPadding: true,
                isButton: true,
                isSelectable: false,
                childPadding: 10,
                automationId: `${this.orgTemplatesAutoIdPrefix}-${node.name}-new-template-button`,
                bundle: node.name
            }));
            this.orgsTree.treeDataUpdated();
            if (this.templateChildHandleToSelect) {
                this.orgsTree.selectNode(node.children.find(c => c.data && c.data.handle === this.templateChildHandleToSelect));
                this.templateChildHandleToSelect = null;
            }
        }
    }

    getTreeData(parentNodeName?: string): void {
        this.restService.post(Endpoint.realmManager_listSystemUserProjectsByUserName, { startIndex: 0, count: 0, userName: this.commonService.currentUser.userName, filters: [], systemScope: this.isSystemContext }).then((response) => {
            if (response && response.userOrgProjects) {
                const treeNodes = new Array<GenericTreeNode>();
                response.userOrgProjects.forEach(org => {
                    let hasCreatePermissions = false;
                    if (org.role) {
                        const permissionsSet = Permissions.mapFromObjectActionList(org.role.objectActionList);
                        hasCreatePermissions = Permissions.checkUserPermission(Permissions.getOrgObjectListTypeForTemplateType(this.templateType as TemplateType), ActionType.CREATE, permissionsSet);
                    }
                    if (hasCreatePermissions) {
                        treeNodes.push(new GenericTreeNode({
                            name: org.organizationName,
                            automationId: `${this.orgTemplatesAutoIdPrefix}-${org.organizationName}`,
                            icon: 'fa-folder',
                            iconExpanded: 'fa-folder-open',
                            iconColor: 'color-yellow-07',
                            isExpandable: true,
                            isExpandedOnSelect: true,
                            hasExpanderPadding: true,
                            requestInfo: new DrRequestInfo({
                                endpoint: Endpoint.orgManager_listTemplates,
                                responseName: 'templates',
                                data: { contextHandle: org.organizationName, scope: 'ORG_LEVEL', tempType: this.templateType, organizationName: org.organizationName }
                            }),
                            createChild: (data: any): GenericTreeNode => {
                                const item = new Template(data);
                                return new GenericTreeNode({
                                    name: item.name,
                                    automationId: `${this.orgTemplatesAutoIdPrefix}-${item.name}`,
                                    customText: item.defaultTemplate ? 'Default' : '',
                                    icon: 'fa-sticky-note',
                                    iconColor: 'color-yellow-07',
                                    childPadding: 10,
                                    hasExpanderPadding: true,
                                    data: item
                                });
                            }
                        }));
                    }
                });
                this.orgsTree.setData(treeNodes);
                if (this.orgsTree.data?.length > 0) {
                    const parentNode = this.orgsTree.data.find(node => node.name === parentNodeName)
                    if (parentNode) {
                        this.orgsTree.selectNode(parentNode);
                    }
                }
            }
        });
    }

    ok(): void {
        if (this.selectedTemplate) {
            this.isBusy = true;
            let parentHandle = this.parentTemplate.handle;
            let selectedHandle = this.selectedTemplate.handle;
            if (this.templateType === TemplateType.LOADFILE_SETTINGS && this.modalData.selectedItem.projectId) {
                parentHandle = this.parentTemplate.id;
                if (this.templateTab === 'project') {
                    selectedHandle = this.selectedTemplate.id;
                }
            }
            if (this.operationType === TemplateOperationType.saveAs) {
                this.isBusyMessage = 'Saving...';
                let requestData = null;
                let endPoint = Endpoint.templateManager_copyToTemplate;
                if (this.isWorkflowSaveToTemplate) {
                    endPoint = Endpoint.workFlowManager_saveWorkflowToTemplate;
                    requestData = { workflowHandle: parentHandle, templateHandle: selectedHandle };
                } else {
                    requestData = { fromHandle: parentHandle, toHandle: selectedHandle, systemScope: this.isSystemContext };
                }
                this.restService.post(endPoint, requestData).then(response => {
                    this.isBusy = false;
                    this.commonService.sendMessage(MessageType.entityChanged, {
                        updateStatusType: UpdateStatusType.TEMPLATE_MEMEBERSHIP,
                        autoSelectHandle: (this.isSystemContext && this.templateTab === 'organization') ? null : selectedHandle
                    });
                    this.closeModal();
                }, () => {
                    this.isBusy = false;
                });
            } else {
                this.isBusyMessage = 'Loading...';
                let requestData = null;
                let endPoint = Endpoint.templateManager_copyFromTemplate;
                if (this.isWorkflowLoadFromTemplate) {
                    requestData = { templateHandle: selectedHandle, projectHandle: parentHandle, missingDataCheck: true };
                    endPoint = Endpoint.workFlowManager_loadWorkflowFromTemplate;
                } else {
                    requestData = { fromHandle: selectedHandle, toHandle: parentHandle, systemScope: this.isSystemContext };
                }
                this.restService.post(endPoint, requestData).then(() => {
                    this.isBusy = false;
                    this.commonService.sendMessage(MessageType.entityChanged, this.templateType);
                    this.closeModal();
                }, error => {
                    this.isBusy = false;
                    if (error?.status === TaskStatus.FAILURE) {
                        this.commonService.openConfirmationWarningModal('At least one Initial Search Target, and/or one or more Tags/Folders are not found in the target project. The missing Initial Search Targets will be set to "Project Data" and any missing Tags/Folders will be discarded.',
                            'Warning', 'Continue Anyway').then((result) => {
                                if (result?.modalResult) {
                                    requestData.missingDataCheck = false;
                                    this.restService.post(Endpoint.workFlowManager_loadWorkflowFromTemplate, requestData).then(() => {
                                        this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.WORK_FLOW);
                                        this.closeModal();
                                    });
                                }
                            })
                    }
                });
            }
        }
    }
}
